
import { supabase } from '@/integrations/supabase/client';
import { saveToLocalStorageFallback, getFromLocalStorage } from '../utils';

export interface Selection {
  type: string;
  name: string;
  price: string;
  dateTime: string;
}

/**
 * Get or create user ID from email for custom authentication system
 */
async function getUserIdFromEmail(email: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id')
      .eq('email', email)
      .maybeSingle();

    if (error || !data) {
      console.error('Error getting user ID from email:', error);
      return null;
    }

    return data.id;
  } catch (err) {
    console.error('Exception when getting user ID:', err);
    return null;
  }
}

/**
 * Function to save selection to Supabase with custom auth
 */
export const saveSelectionToSupabase = async (selection: Selection, userEmail?: string): Promise<boolean> => {
  try {
    if (!userEmail) {
      console.error('No user email provided for saving selection');
      saveToLocalStorageFallback('selections', selection);
      return false;
    }

    const userId = await getUserIdFromEmail(userEmail);
    if (!userId) {
      console.error('No user ID found for email:', userEmail);
      saveToLocalStorageFallback('selections', selection);
      return false;
    }

    // Map the selection object to match Supabase table column names
    const selectionData = {
      type: selection.type,
      name: selection.name,
      price: selection.price,
      datetime: selection.dateTime,
      user_id: userId
    };
    
    const { error } = await supabase
      .from('selections')
      .insert([selectionData]);
    
    if (error) {
      console.error('Error saving selection to Supabase:', error);
      saveToLocalStorageFallback('selections', selection);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error('Exception when saving selection to Supabase:', err);
    saveToLocalStorageFallback('selections', selection);
    return false;
  }
};

/**
 * Function to get selections from Supabase with custom auth
 */
export const getSelectionsFromSupabase = async (date?: Date, userEmail?: string) => {
  try {
    if (!userEmail) {
      console.log('No user email provided, falling back to localStorage');
      return getFromLocalStorage<Selection>('selections');
    }

    const userId = await getUserIdFromEmail(userEmail);
    if (!userId) {
      console.log('No user ID found, falling back to localStorage');
      return getFromLocalStorage<Selection>('selections');
    }

    let query = supabase
      .from('selections')
      .select('*')
      .eq('user_id', userId)
      .order('datetime', { ascending: false });
    
    if (date) {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      
      query = query
        .gte('datetime', startOfDay.toISOString())
        .lte('datetime', endOfDay.toISOString());
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error getting selections from Supabase:', error);
      return getFromLocalStorage<Selection>('selections');
    }
    
    return data || [];
  } catch (err) {
    console.error('Exception when getting selections from Supabase:', err);
    return getFromLocalStorage<Selection>('selections');
  }
};
